import React, { useEffect, useRef, useState } from "react";
import {
  useParams,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { Controller, Path, useForm, useWatch } from "react-hook-form";

import Button from "components/atoms/Button/Button";
import Input from "components/atoms/Input/Input";
import { Layout } from "components/Layout.tsx/Layout";
import { Tabs } from "components/Tabs/Tabs";
import CustomSelect from "components/atoms/CustomSelect/CustomSelect";
import { Modal } from "components/Modal/Modal";
import ErrorModalContent from "components/ErrorModalContent/ErrorModalContent";
import { Info } from "components/atoms/Info/Info";
import { Footer } from "components/Footer/Footer";

import {
  Checkbox,
  Container,
  MainContainer,
  Wrapper,
  LabelWrapper,
  Label,
  Section,
  SectionTitle,
  SubmitButtonContainer,
  ModalActions,
  EditText,
  ErrorText,
} from "./styles";

import {
  articleWebsiteFields,
  commonSettingsFields,
  configDataToFormValues,
  defaultFormValues,
  promptsFieldsByConfig,
} from "./configurationSettings";

import { ContentType, DataObject } from "utils/types";
import { extractDataByType, extractDiscordFieldsFromCurl } from "utils/helpers";
import { useAxiosPrivate } from "hooks/api/useAxiosPrivate";
import Loader from "components/Loader/Loader";
import SuccessModalContent from "components/SuccessModalContent/SuccessModalContent";

//TODO: TO BE REFACTORED
export const AddConfiguration: React.FC = () => {
  const VALID_TABS: ContentType[] = ["post", "pin", "article"];

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("type");
  const initialTab = VALID_TABS.includes(tabParam as ContentType)
    ? (tabParam as ContentType)
    : "post";

  const { id } = useParams<{ id?: string }>();
  const matchEdit = useMatch("/configuration/edit/:id");
  const isEditMode = Boolean(matchEdit && id);
  const navigate = useNavigate();

  const [curlInput, setCurlInput] = useState("");
  const [activeConfig, setActiveConfig] = useState<ContentType>(initialTab);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isCurlParseError, setIsCurlParseError] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState<{
    [field: string]: string;
  }>({});

  const topRef = useRef<HTMLDivElement>(null);

  const axiosPrivate = useAxiosPrivate();

  const {
    data: configurationData,
    isLoading: isFetchingConfiguration,
    isError: isConfigError,
    error: configError,
  } = useQuery(
    ["configuration-byId", id],
    async () => {
      const response = await axiosPrivate.get(`/configuration/${id}`);
      return response.data;
    },
    {
      enabled: isEditMode && Boolean(id),
    }
  );

  const mutation = useMutation(
    (formattedData: any) => {
      if (isEditMode && id) {
        return axiosPrivate.put(`/configuration/edit/${id}`, formattedData);
      } else {
        return axiosPrivate.post("/configuration/add", formattedData);
      }
    },
    {
      onSuccess: (data) => {
        setSuccessModalOpen(true);
      },
      onError: (error) => {
        const err: any = error;
        if (err.response && err.response.data && err.response.data.error) {
          try {
            const parsedError = JSON.parse(err.response.data.error);

            if (parsedError.errors) {
              setErrorMessages(parsedError.errors);
            } else {
              setErrorMessages({ "error details": parsedError });
            }
          } catch (parseError) {
            setErrorMessages({ "error details": err.response.data.error });
          }
        } else {
          setErrorMessages({ "error details": "An unknown error occurred." });
        }

        setErrorModalOpen(true);
      },
    }
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<DataObject>({
    mode: "onBlur", // or maybe "onChange" as needed ?
    defaultValues: defaultFormValues,
  });

  const customPromptUsed = useWatch({
    control,
    name: `prompts.${activeConfig}.customPromptUsed` as Path<DataObject>,
  });

  const currentNiche = useWatch({
    control,
    name: `prompts.${activeConfig}.niche` as Path<DataObject>,
  });

  const getFieldVisibility = (fieldName: string) => {
    if (fieldName === "articleLength" && activeConfig === "article") {
      // Use form state if available (user has interacted), otherwise use API data
      const shouldHide =
        customPromptUsed !== undefined
          ? customPromptUsed
          : isEditMode && configurationData
            ? configurationData.customPromptUsed
            : false;
      return !shouldHide; // return true if should show, false if should hide
    }

    if (fieldName === "articlePrompt" && activeConfig === "article") {
      // Use form state if available (user has interacted), otherwise use API data
      const shouldShow =
        customPromptUsed !== undefined
          ? customPromptUsed
          : isEditMode && configurationData
            ? configurationData.customPromptUsed
            : false;
      return shouldShow; // return true if should show, false if should hide
    }

    return true; // default: show field
  };

  useEffect(() => {
    if (!isEditMode) {
      const currentTab = searchParams.get("type");

      if (!VALID_TABS.includes(currentTab as ContentType)) {
        setSearchParams({ type: "post" }, { replace: true });
        setActiveConfig("post");
      } else if (currentTab !== activeConfig) {
        setActiveConfig(currentTab as ContentType);
      }
    }
  }, [searchParams, setSearchParams, activeConfig]);

  useEffect(() => {
    if (isConfigError) {
      const err: any = configError;
      let messages = { "error details": "An unknown error occurred." };

      if (err.response && err.response.data && err.response.data.error) {
        try {
          const parsedError = JSON.parse(err.response.data.error);
          messages = parsedError.errors
            ? parsedError.errors
            : { "error details": parsedError };
        } catch (parseError) {
          messages = { "error details": err.response.data.error };
        }
      }
      setErrorMessages(messages);
      setErrorModalOpen(true);
    }
  }, [isConfigError, configError]);

  useEffect(() => {
    if (isEditMode && configurationData) {
      setActiveConfig(configurationData.type);
      reset(configDataToFormValues(configurationData));
    }

    if (!isEditMode) {
      reset(defaultFormValues);
    }
  }, [isEditMode, configurationData, reset]);

  const onSubmit = (data: DataObject) => {
    const formattedData = extractDataByType(activeConfig, data);
    mutation.mutate(formattedData);
  };

  const handleTabChange = (newTab: ContentType) => {
    setActiveConfig(newTab);
    reset(defaultFormValues);
    clearErrors();

    setSearchParams({ type: newTab });

    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleParseCurl = () => {
    const parsedData = extractDiscordFieldsFromCurl(curlInput);

    if (Object.keys(parsedData).length === 0) {
      setIsCurlParseError(true);
    } else {
      setIsCurlParseError(false);
      const currentValues = getValues();

      const updatedCommonSettings = {
        ...currentValues.commonSettings,
        midjourneyToken: parsedData.authorization || "",
        applicationId: parsedData.application_id || "",
        sessionId: parsedData.session_id || "",
        serverId: parsedData.guild_id || "",
        channelId: parsedData.channel_id || "",
      };

      reset({
        ...currentValues,
        commonSettings: updatedCommonSettings,
      });

      setIsModalOpen(false);
      setCurlInput("");
    }
  };

  const renderCommonSettings = () => {
    return (
      <MainContainer>
        {commonSettingsFields.map((field, idx) => {
          const fieldName =
            `commonSettings.${field.name}` as unknown as Path<DataObject>;

          return (
            <Wrapper key={idx}>
              <Label htmlFor={field.name}>{field.label}</Label>
              <Input
                id={field.name}
                type={field.type}
                placeholder={field.placeholder}
                {...register(fieldName, {
                  required: `${field.label} is required`,
                })}
              />
              {errors.commonSettings &&
                (errors.commonSettings as any)[field.name] && (
                  <ErrorText>
                    {
                      (errors.commonSettings as any)[field.name]
                        .message as string
                    }
                  </ErrorText>
                )}
            </Wrapper>
          );
        })}
        <Wrapper>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setIsModalOpen(true)}
          >
            Parse CURL Command (Midjourney)
          </Button>
        </Wrapper>
      </MainContainer>
    );
  };

  const renderWebsiteSettings = () => {
    return (
      <MainContainer>
        {articleWebsiteFields.map((field, idx) => {
          // Build the field name as e.g., "websiteSettings.websiteUrl"
          const fieldName =
            `websiteSettings.${field.name}` as unknown as Path<DataObject>;
          return (
            <Wrapper key={idx}>
              <Label htmlFor={field.name}>{field.label}</Label>
              <Input
                id={field.name}
                type={field.type}
                placeholder={field.placeholder}
                $halfWidth={idx === 2}
                {...register(fieldName, {
                  required: `${field.label} is required`,
                })}
              />
              {errors.websiteSettings?.[
                field.name as keyof DataObject["websiteSettings"]
              ] && (
                <ErrorText>
                  {
                    errors.websiteSettings?.[
                      field.name as keyof DataObject["websiteSettings"]
                    ]?.message as string
                  }
                </ErrorText>
              )}
            </Wrapper>
          );
        })}
      </MainContainer>
    );
  };

  const renderPrompts = () => {
    const fields = promptsFieldsByConfig[activeConfig] || [];
    return (
      <MainContainer>
        {fields.map((field, idx) => {
          // Build the field name as a nested path: e.g., "prompts.post.outputType"
          const fieldName =
            `prompts.${activeConfig}.${field.name}` as unknown as Path<DataObject>;
          const validation = { required: `${field.label} is required` };

          if (field.type === "select") {
            // Hide articleLength field if customPromptUsed is checked
            if (field.name === "articleLength" && activeConfig === "article") {
              if (!getFieldVisibility(field.name)) {
                return null;
              }
            }
            return (
              <Wrapper key={idx}>
                <Label htmlFor={field.name}>{field.label}</Label>
                <Controller
                  control={control}
                  name={fieldName}
                  rules={validation}
                  render={({ field: { onChange, value, ref } }) => (
                    <CustomSelect
                      id={field.name}
                      options={field.options}
                      value={value as string | number | string[]}
                      onChange={onChange}
                    />
                  )}
                />
                {(errors.prompts?.[activeConfig] as any)?.[field.name] && (
                  <ErrorText>
                    {
                      (errors.prompts?.[activeConfig] as any)?.[field.name]
                        ?.message as string
                    }
                  </ErrorText>
                )}
              </Wrapper>
            );
          } else if (field.type === "checkbox") {
            return (
              <Wrapper key={idx} $isAligned={true}>
                <Label htmlFor={field.name}>
                  <Controller
                    control={control}
                    name={fieldName}
                    render={({ field: { onChange, value, ref } }) => (
                      <Checkbox
                        id={field.name}
                        onChange={onChange}
                        checked={!!value}
                        ref={ref}
                      />
                    )}
                  />{" "}
                  {field.label}
                </Label>
                {(errors.prompts?.[activeConfig] as any)?.[field.name] && (
                  <ErrorText>
                    {
                      (errors.prompts?.[activeConfig] as any)?.[field.name]
                        ?.message as string
                    }
                  </ErrorText>
                )}
              </Wrapper>
            );
          } else if (field.type === "edittext") {
            // Hide articlePrompt field if customPromptUsed is not checked
            if (field.name === "articlePrompt" && activeConfig === "article") {
              if (!getFieldVisibility(field.name)) {
                return null;
              }
              // Add custom validation for articlePrompt
              const customValidation = {
                required: `${field.label} is required`,

                validate: (value: any) => {
                  let requiredVars = [
                    "{focus_keyphrase}",
                    "{internal_links}",
                    "{external_link}",
                  ];

                  // Add recipe_ingredients only if niche is RECIPE
                  if (currentNiche === "RECIPE") {
                    requiredVars.push("{recipe_ingredients}");
                  }

                  const missingVars = requiredVars.filter(
                    (varName) => !value.includes(varName)
                  );
                  if (missingVars.length > 0) {
                    return `Missing required variables: ${missingVars.join(", ")}`;
                  }
                  return true;
                },
              };

              return (
                <Wrapper key={idx}>
                  <LabelWrapper>
                    <Label htmlFor={field.name}>{field.label}</Label>
                    {field.hasInfo && field.info && (
                      <Info details={field.info} />
                    )}
                  </LabelWrapper>
                  <Controller
                    control={control}
                    name={fieldName}
                    rules={customValidation}
                    render={({ field: { onChange, value, ref } }) => (
                      <EditText
                        id={field.name}
                        placeholder={field.placeholder}
                        value={value as string}
                        onChange={onChange}
                        ref={ref}
                      />
                    )}
                  />
                  {(errors.prompts?.[activeConfig] as any)?.[field.name] && (
                    <ErrorText>
                      {
                        (errors.prompts?.[activeConfig] as any)?.[field.name]
                          ?.message as string
                      }
                    </ErrorText>
                  )}
                </Wrapper>
              );
            }

            return (
              <Wrapper key={idx}>
                <LabelWrapper>
                  <Label htmlFor={field.name}>{field.label}</Label>
                  {field.hasInfo && field.info && <Info details={field.info} />}
                </LabelWrapper>
                <Controller
                  control={control}
                  name={fieldName}
                  rules={validation}
                  render={({ field: { onChange, value, ref } }) => (
                    <EditText
                      id={field.name}
                      placeholder={field.placeholder}
                      value={value as string}
                      onChange={onChange}
                      ref={ref}
                    />
                  )}
                />
                {(errors.prompts?.[activeConfig] as any)?.[field.name] && (
                  <ErrorText>
                    {
                      (errors.prompts?.[activeConfig] as any)?.[field.name]
                        ?.message as string
                    }
                  </ErrorText>
                )}
              </Wrapper>
            );
          } else {
            return (
              <Wrapper key={idx}>
                <Label htmlFor={field.name}>{field.label}</Label>
                <Input
                  id={field.name}
                  type={field.type}
                  placeholder={field.placeholder}
                  {...register(fieldName, validation)}
                />
                {(errors.prompts?.[activeConfig] as any)?.[field.name] && (
                  <ErrorText>
                    {
                      (errors.prompts?.[activeConfig] as any)?.[field.name]
                        ?.message as string
                    }
                  </ErrorText>
                )}
              </Wrapper>
            );
          }
        })}
      </MainContainer>
    );
  };

  if (mutation.isLoading || isFetchingConfiguration) {
    return <Loader />;
  }

  return (
    <Layout>
      <div ref={topRef} />
      {!isConfigError && (
        <Container>
          {!isEditMode ? (
            <Tabs
              tabs={["post", "pin", "article"]}
              activeTab={activeConfig}
              onTabChange={(tab: string) => handleTabChange(tab as ContentType)}
            />
          ) : (
            configurationData.type && (
              <SectionTitle>
                Edit {configurationData.type} Configuration:
              </SectionTitle>
            )
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div key={activeConfig}>
              <Section>
                <SectionTitle>{`${activeConfig} Configuration Title:`}</SectionTitle>
                <Input
                  id="title"
                  type="text"
                  placeholder="Enter The configuration title"
                  {...register(`title.${activeConfig}` as Path<DataObject>, {
                    required: "Configuration title is required",
                  })}
                />
                {errors.title && errors.title[activeConfig] && (
                  <ErrorText>
                    {errors.title[activeConfig]?.message as string}
                  </ErrorText>
                )}
              </Section>
              <Section>
                <SectionTitle>
                  {`${activeConfig} Configuration Prompts:`}
                </SectionTitle>
                {renderPrompts()}
              </Section>
              {activeConfig === "article" && (
                <Section>
                  <SectionTitle>
                    {`${activeConfig} Configuration Website:`}
                  </SectionTitle>
                  {renderWebsiteSettings()}
                </Section>
              )}
              <Section>
                <SectionTitle>
                  {`${activeConfig} Configuration OpenAI / Midjourney:`}
                </SectionTitle>
                {renderCommonSettings()}
              </Section>
              <SubmitButtonContainer>
                <Button type="submit">Submit Configuration</Button>
              </SubmitButtonContainer>
            </div>
          </form>
        </Container>
      )}

      {isModalOpen && (
        <Modal onClose={() => setIsModalOpen(false)}>
          <div>
            <h2>Parse CURL Command (Midjourney)</h2>
            <EditText
              placeholder="Paste CURL Command Midjourney here"
              value={curlInput}
              onChange={(e) => setCurlInput(e.target.value)}
            />
            {isCurlParseError && (
              <ErrorText>
                Parsing failed ! Please make sure you put a valid CURL Command
                from Midjourney Discord ( As described in the documentation ) !
              </ErrorText>
            )}
            <ModalActions>
              <Button onClick={handleParseCurl}>
                Parse Midjourney CURL Command
              </Button>
            </ModalActions>
          </div>
        </Modal>
      )}
      {errorModalOpen && (
        <Modal
          onClose={() => {
            setErrorModalOpen(false);
            if (isConfigError) navigate("/configuration/list");
          }}
        >
          <ErrorModalContent
            errorMessages={errorMessages}
            onClose={() => {
              setErrorModalOpen(false);
              if (isConfigError) navigate("/configuration/list");
            }}
          />
        </Modal>
      )}
      {successModalOpen && (
        <Modal onClose={() => setSuccessModalOpen(false)}>
          <SuccessModalContent
            successMessage={`${isEditMode ? "Configuration updated successfully!" : "Configuration added successfully!"}`}
            onClose={() => navigate("/configuration/list")}
          />
        </Modal>
      )}
      <Footer />
    </Layout>
  );
};
